/**
 * Word-level alignment between expected sentence and ASR transcript.
 * Pure JS — no DOM / browser APIs.
 */

export type WordTag = 'ok' | 'near' | 'wrong' | 'missing' | 'extra';

export type WordResult = {
  expected: string;
  heard: string;
  tag: WordTag;
};

export type DiffResult = {
  words: WordResult[];
  scorePct: number;
  okCount: number;
  totalExpected: number;
  wrongWords: string[];
};

export function normalizeWord(raw: string): string {
  return String(raw || '')
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[^a-z0-9']/g, '')
    .trim();
}

export function tokenize(text: string): string[] {
  return String(text || '')
    .split(/\s+/)
    .map(normalizeWord)
    .filter(Boolean);
}

function charDistance(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const prev = new Array(n + 1);
  const cur = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    cur[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = cur[j];
  }
  return prev[n];
}

function tagPair(expected: string, heard: string): WordTag {
  if (expected === heard) return 'ok';
  if (!heard) return 'missing';
  if (!expected) return 'extra';
  const d = charDistance(expected, heard);
  const maxLen = Math.max(expected.length, heard.length);
  if (d <= 1 || (maxLen >= 5 && d <= 2)) return 'near';
  return 'wrong';
}

/**
 * Align expected words to heard words via Levenshtein on the word sequence.
 */
export function alignWords(expectedText: string, heardText: string): DiffResult {
  const exp = tokenize(expectedText);
  const heard = tokenize(heardText);
  const m = exp.length;
  const n = heard.length;

  // DP costs
  const dp: number[][] = [];
  for (let i = 0; i <= m; i++) {
    dp[i] = [];
    for (let j = 0; j <= n; j++) {
      if (i === 0) dp[i][j] = j;
      else if (j === 0) dp[i][j] = i;
      else {
        const cost = exp[i - 1] === heard[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + cost
        );
      }
    }
  }

  // Backtrack
  const pairs: { e: string; h: string }[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0) {
      const cost = exp[i - 1] === heard[j - 1] ? 0 : 1;
      if (dp[i][j] === dp[i - 1][j - 1] + cost) {
        pairs.push({ e: exp[i - 1], h: heard[j - 1] });
        i--;
        j--;
        continue;
      }
    }
    if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
      pairs.push({ e: exp[i - 1], h: '' });
      i--;
      continue;
    }
    if (j > 0 && dp[i][j] === dp[i][j - 1] + 1) {
      pairs.push({ e: '', h: heard[j - 1] });
      j--;
      continue;
    }
    // fallback
    if (i > 0 && j > 0) {
      pairs.push({ e: exp[i - 1], h: heard[j - 1] });
      i--;
      j--;
    } else if (i > 0) {
      pairs.push({ e: exp[i - 1], h: '' });
      i--;
    } else {
      pairs.push({ e: '', h: heard[j - 1] });
      j--;
    }
  }
  pairs.reverse();

  const words: WordResult[] = pairs.map((p) => ({
    expected: p.e,
    heard: p.h,
    tag: tagPair(p.e, p.h),
  }));

  const expectedWords = words.filter((w) => w.expected);
  const okCount = expectedWords.filter((w) => w.tag === 'ok' || w.tag === 'near').length;
  // Score: ok full + near half credit
  let points = 0;
  expectedWords.forEach((w) => {
    if (w.tag === 'ok') points += 1;
    else if (w.tag === 'near') points += 0.5;
  });
  const totalExpected = expectedWords.length || 1;
  const scorePct = Math.round((points / totalExpected) * 100);

  const wrongWords = expectedWords
    .filter((w) => w.tag === 'wrong' || w.tag === 'missing' || w.tag === 'near')
    .map((w) => w.expected)
    .filter((w, idx, arr) => arr.indexOf(w) === idx);

  return {
    words,
    scorePct,
    okCount: expectedWords.filter((w) => w.tag === 'ok').length,
    totalExpected: expectedWords.length,
    wrongWords,
  };
}
